// src/hooks/usePlans.js — S-161: THE canonical plans fetcher (Rule H)
//
// WHY THIS EXISTS
// ───────────────
// Two components need the same data and got it two different ways:
//
//   PremiumPage.jsx  — fetched subscriptions.plans() in its own useEffect ✅
//   PremiumLock.jsx  — HARDCODED it. Every value. ❌
//
// PremiumLock's hardcoded array was wrong in two ways at once:
//
//   1. PRICES. It rendered `price:'₹29' / '₹299' / '₹799' / '₹1499'` with NO
//      country check — so a user in the US, Germany, Korea or Nigeria opened the
//      paywall and was quoted INDIAN RUPEES. On the conversion moment. It fires
//      from AIAssistant.jsx on quota limits, so it is not a rare path.
//      (This is the S-146 ONE-PRICE-ONE-SOURCE rule, broken.)
//
//   2. PORTFOLIO LIMITS. `portfolio:'$25K' / '$350K' / '$650K' / '$1M'` were
//      typed as literal strings, so S-161's tier change (45K/630K/1.2M/1.8M)
//      would have left the paywall advertising the OLD numbers while the backend
//      granted the new ones. The paywall would lie.
//
// Fixing that by "updating the strings" just re-syncs a duplicate that drifts
// again on the next change. The standard answer is the one React has had since
// hooks: duplicated useState+useEffect across components => extract a hook.
// This codebase already uses that pattern — see hooks/usePremium.js.
//
// CACHE
// ─────
// Module-scoped, deliberately. The paywall (PremiumLock) is what LEADS TO
// PremiumPage, so it fetches first and warms the cache — PremiumPage then
// renders instantly instead of doing its own round-trip. Net faster than before,
// not slower. The data is per-session static (server-detected country + regional
// prices); it does not need revalidation mid-session.
//
// The backend is authoritative for country (profile → IP → header, server-side),
// so a client cannot spoof itself into a cheaper region.

import { useState, useEffect } from 'react';
import { subscriptions } from '../services/api';

// ── module-scoped cache: survives unmount, shared by every consumer ──
let _cache = null;      // { plans, country, region }
let _inflight = null;   // dedupes concurrent callers (paywall + page at once)

export function primePlansCache(data) { _cache = data; }   // for tests
export function clearPlansCache()      { _cache = null; _inflight = null; }

export function usePlans() {
  const [data, setData]       = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) { setData(_cache); setLoading(false); return; }

    let alive = true;
    // Reuse an in-flight request rather than firing a second one — the paywall
    // and the page can mount within milliseconds of each other.
    _inflight = _inflight || subscriptions.plans();

    _inflight
      .then((r) => {
        if (r && r.success) {
          _cache = { plans: r.plans || [], country: r.country || null, region: r.region || null };
          if (alive) setData(_cache);
        }
      })
      .catch(() => { /* leave data null — consumers render their own empty state */ })
      .finally(() => { _inflight = null; if (alive) setLoading(false); });

    return () => { alive = false; };
  }, []);

  return {
    plans:   data?.plans   || [],
    country: data?.country || null,
    region:  data?.region  || null,
    loading,
  };
}

/**
 * The one place a plan's price is turned into text.
 *
 * Priority: regional_price.display (backend-computed, correct currency)
 *        → price_usd (per-region since S-160F — NOT the old flat $3.59)
 *        → '' (never a hardcoded ₹)
 */
export function planPriceText(plan) {
  if (plan?.regional_price?.display) return plan.regional_price.display;
  if (plan?.price_usd != null)       return `$${plan.price_usd}`;
  return '';
}

/** Portfolio limit → short display text. 45000 → "$45K", 1800000 → "$1.8M". */
export function portfolioText(limit) {
  const n = Number(limit) || 0;
  if (n >= 1000000) {
    const m = n / 1000000;
    return `$${m % 1 === 0 ? m : m.toFixed(1)}M`;
  }
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
}
